import { REPORT_KINDS, type ReportKind, type ReportView } from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Printer } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime, tenge, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { qty, warehousesQuery } from './queries';

const reportQuery = (kind: ReportKind, q: Record<string, string | undefined>) =>
  queryOptions({
    queryKey: ['wh', 'report', kind, q],
    queryFn: () => api<ReportView>(`/warehouse/reports/${kind}`, { query: q }),
  });

function cell(v: string | number | null, kind: ReportView['columns'][number]['kind']): string {
  if (v === null || v === undefined || v === '') return '—';
  if ((kind === 'date' || kind === 'datetime') && Number.isNaN(new Date(String(v)).getTime()))
    return String(v);
  if (kind === 'money') return tenge(Number(v));
  if (kind === 'num') return qty(Number(v));
  if (kind === 'date') return date(String(v));
  if (kind === 'datetime') return dateTime(String(v));
  return String(v);
}

/** Отчёты за период: движение, использование инструмента, ремонты */
const PERIOD_KINDS = new Set<ReportKind>(['movements', 'tool_usage', 'repairs', 'customer']);

/** Отчёты склада (СК-36): на экране и в Excel; «Остатки на дату» на конец месяца — сохранённый снимок */
export function ReportsTab() {
  const warehouses = useQuery(warehousesQuery);
  const [kind, setKind] = useState<ReportKind>('stock');
  const [warehouseId, setWarehouseId] = useState('');
  const [asOf, setAsOf] = useState(todayLocal());
  const [from, setFrom] = useState(`${todayLocal().slice(0, 7)}-01`);
  const [to, setTo] = useState(todayLocal());
  const [days, setDays] = useState('');
  const [threshold, setThreshold] = useState('');
  const qc = useQueryClient();
  // Срок «без движения» (СК-17) — настройка компании; меняет кладовщик с правом на уровне компании
  const settings = useQuery({
    queryKey: ['wh', 'surplus-settings'],
    queryFn: () => api<{ days: number }>('/warehouse/surplus/settings'),
    enabled: kind === 'no_movement',
  });
  // Порог перерасхода (СК-22) — настройка компании
  const consumptionSettings = useQuery({
    queryKey: ['wh', 'consumption-settings'],
    queryFn: () => api<{ thresholdPct: number }>('/warehouse/consumption/settings'),
    enabled: kind === 'norm_consumption',
  });
  // СК-22: задачи руководителям проектов по материалам с перерасходом выше порога
  const raiseTasks = useMutation({
    mutationFn: () => {
      const pid = (warehouses.data ?? []).find((w) => w.id === warehouseId)?.projectId;
      return api<{ created: number }>('/warehouse/reports/norm_consumption/tasks', {
        method: 'POST',
        query: pid ? { projectId: pid } : undefined,
      });
    },
    onSuccess: (v) =>
      toast(
        v.created
          ? `Поставлено задач: ${v.created}`
          : 'Новых задач нет: перерасхода выше порога нет или задачи уже стоят',
      ),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const canSetCompany = (warehouses.data ?? []).some((w) => w.canOperate && !w.projectId);
  const saveDays = useMutation({
    mutationFn: (d: number) =>
      api<{ days: number }>('/warehouse/surplus/settings', { method: 'PUT', body: { days: d } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast('Срок сохранён');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const saveThreshold = useMutation({
    mutationFn: (pct: number) =>
      api<{ thresholdPct: number }>('/warehouse/consumption/settings', {
        method: 'PUT',
        body: { thresholdPct: pct },
      }),
    onSuccess: (v) => {
      setThreshold('');
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(`Порог сохранён: ${String(v.thresholdPct).replace('.', ',')} %`);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const thresholdNum = (() => {
    const raw = threshold.trim().replace(',', '.');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
  })();
  const thresholdReady =
    thresholdNum !== null &&
    thresholdNum >= 0.1 &&
    thresholdNum <= 100 &&
    thresholdNum !== consumptionSettings.data?.thresholdPct;
  const q: Record<string, string | undefined> = {
    warehouseId: kind !== 'replenish' && warehouseId ? warehouseId : undefined,
    asOf: kind === 'stock' && asOf ? asOf : undefined,
    from: PERIOD_KINDS.has(kind) && from ? from : undefined,
    to: PERIOD_KINDS.has(kind) && to ? to : undefined,
    days:
      (kind === 'no_movement' || kind === 'checks') && /^\d+$/.test(days.trim()) ? days.trim() : undefined,
  };
  const r = useQuery(reportQuery(kind, q));
  // Ключи строк: у строк отчёта нет своего идентификатора, поэтому порядковый номер считается заранее
  const keyed = (r.data?.rows ?? []).map((row, n) => ({
    row,
    key: `${n}:${String(row[r.data?.columns[0]?.key ?? ''] ?? '')}`,
  }));
  // Печатный отчёт заказчику — по одному складу заказчика (СК-25)
  const customerWh = (warehouses.data ?? []).find((w) => w.id === warehouseId && w.kind === 'customer');
  const qs = new URLSearchParams(
    Object.entries(q).filter((e): e is [string, string] => Boolean(e[1])),
  ).toString();
  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Отчёт">
          <Select
            value={kind}
            onChange={(e) => {
              const k = e.target.value as ReportKind;
              // У отчёта заказчику свой список складов — прежний выбор сбрасывается
              if ((k === 'customer') !== (kind === 'customer')) setWarehouseId('');
              setKind(k);
            }}
          >
            {(Object.keys(REPORT_KINDS) as ReportKind[]).map((k) => (
              <option key={k} value={k}>
                {REPORT_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {kind !== 'replenish' ? (
          <Field label="Склад">
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">{kind === 'customer' ? 'Все склады заказчиков' : 'Все склады'}</option>
              {(warehouses.data ?? [])
                .filter((w) => kind !== 'customer' || w.kind === 'customer')
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
            </Select>
          </Field>
        ) : null}
        {kind === 'stock' ? (
          <Field label="На дату" hint="конец месяца — сохранённый снимок">
            <Input type="date" max={todayLocal()} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
        ) : null}
        {PERIOD_KINDS.has(kind) ? (
          <>
            <Field label="С">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="По">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </>
        ) : null}
        {kind === 'no_movement' ? (
          <>
            <Field
              label="Дней без движения"
              hint={
                settings.data ? `по настройке компании — ${settings.data.days}` : 'срок — настройка компании'
              }
            >
              <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} />
            </Field>
            {canSetCompany && /^\d+$/.test(days.trim()) && Number(days) !== settings.data?.days ? (
              <Button
                variant="secondary"
                loading={saveDays.isPending}
                onClick={() => saveDays.mutate(Number(days))}
              >
                Сделать сроком компании
              </Button>
            ) : null}
          </>
        ) : null}
        {kind === 'norm_consumption' ? (
          <>
            <Field
              label="Порог перерасхода, %"
              hint={
                consumptionSettings.data
                  ? `по настройке компании — ${String(consumptionSettings.data.thresholdPct).replace('.', ',')} %`
                  : 'по умолчанию 1,5 %'
              }
            >
              <Input
                inputMode="decimal"
                placeholder={
                  consumptionSettings.data
                    ? String(consumptionSettings.data.thresholdPct).replace('.', ',')
                    : '1,5'
                }
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
              />
            </Field>
            {canSetCompany && thresholdReady ? (
              <Button
                variant="secondary"
                loading={saveThreshold.isPending}
                onClick={() => saveThreshold.mutate(thresholdNum!)}
              >
                Сделать порогом компании
              </Button>
            ) : null}
          </>
        ) : null}
        {kind === 'checks' ? (
          <Field label="Горизонт, дней" hint="по умолчанию 30">
            <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} />
          </Field>
        ) : null}
      </div>
      <Panel
        title={r.data?.title ?? REPORT_KINDS[kind]}
        meta={r.data?.subtitle ?? undefined}
        actions={
          <div className="flex flex-wrap gap-2">
            {kind === 'norm_consumption' ? (
              <Button variant="secondary" loading={raiseTasks.isPending} onClick={() => raiseTasks.mutate()}>
                Поставить задачи по перерасходу
              </Button>
            ) : null}
            {kind === 'customer' && customerWh ? (
              <a
                href={`/api/v1/warehouse/reports/customer.html?${qs}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
              >
                <Printer className="size-4" aria-hidden />
                Отчёт заказчику
              </a>
            ) : null}
            <a
              href={`/api/v1/warehouse/reports/${kind}.xlsx${qs ? `?${qs}` : ''}`}
              className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
            >
              <Download className="size-4" aria-hidden />
              Excel
            </a>
          </div>
        }
        bodyClassName="p-0"
      >
        {r.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : r.isError ? (
          <ErrorBox error={r.error} className="m-5" />
        ) : r.data.rows.length ? (
          <>
            <ul className="divide-y divide-line md:hidden">
              {keyed.map(({ row, key }) => (
                <li key={key} className="flex flex-col gap-0.5 px-4 py-3 text-[13px]">
                  {r.data.columns.map((c, ci) => (
                    <span
                      key={c.key}
                      className={
                        ci === 0
                          ? 'text-[14px] font-medium text-ink'
                          : 'flex justify-between gap-3 text-ink-2'
                      }
                    >
                      {ci === 0 ? (
                        cell(row[c.key] ?? null, c.kind)
                      ) : (
                        <>
                          <span>{c.title}</span>
                          <span className="num text-ink">{cell(row[c.key] ?? null, c.kind)}</span>
                        </>
                      )}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[900px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    {r.data.columns.map((c, i) => (
                      <th
                        key={c.key}
                        className={`py-2.5 pr-3 font-medium ${i === 0 ? 'pl-5' : ''} ${c.kind === 'num' || c.kind === 'money' ? 'text-right' : ''}`}
                      >
                        {c.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keyed.map(({ row, key }) => (
                    <tr key={key} className="border-t border-line">
                      {r.data.columns.map((c, ci) => (
                        <td
                          key={c.key}
                          className={`py-2 pr-3 ${ci === 0 ? 'pl-5' : ''} ${c.kind === 'num' || c.kind === 'money' ? 'num text-right' : ''}`}
                        >
                          {cell(row[c.key] ?? null, c.kind)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {Object.keys(r.data.totals).length ? (
                    <tr className="border-t border-line font-semibold">
                      {r.data.columns.map((c, ci) => (
                        <td
                          key={c.key}
                          className={`py-2 pr-3 ${ci === 0 ? 'pl-5' : ''} ${c.kind === 'num' || c.kind === 'money' ? 'num text-right' : ''}`}
                        >
                          {ci === 0
                            ? 'Итого'
                            : r.data.totals[c.key] !== undefined
                              ? cell(r.data.totals[c.key]!, c.kind)
                              : ''}
                        </td>
                      ))}
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </ScrollX>
          </>
        ) : (
          <div className="p-5">
            <EmptyState title="Пусто">По выбранным условиям строк нет.</EmptyState>
          </div>
        )}
      </Panel>
    </>
  );
}
